<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { format } from 'date-fns'
  import Button from '@/components/ui/button/Button.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { Loader2, Sparkles } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast/use-toast'
  import { usePlaceholders } from '@/composables/usePlaceholders'
  import { ROUTES } from '@/router/routes'
  import { Separator } from '@/components/ui/separator'

  const router = useRouter()
  const route = useRoute()
  const { toast } = useToast()
  const {
    placeholder,
    isLoading,
    fetchPlaceholder,
    updatePlaceholder,
    generateLabelsForPlaceholder
  } = usePlaceholders()

  const isSaving = ref(false)
  const isGenerating = ref(false)

  const form = ref({
    placeholder_name: '',
    german_label: '',
    english_label: ''
  })

  const originalForm = ref({
    placeholder_name: '',
    german_label: '',
    english_label: ''
  })

  const isFormValid = computed(() => {
    return form.value.placeholder_name.trim() !== ''
  })

  const hasChanges = computed(() => {
    return (
      form.value.german_label !== originalForm.value.german_label ||
      form.value.english_label !== originalForm.value.english_label
    )
  })

  const formatDate = (date: string) => {
    return format(new Date(date), 'MMM d, yyyy HH:mm')
  }

  const navigateBack = () => {
    router.push(ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS)
  }

  const generateLabels = async () => {
    if (!form.value.placeholder_name) return

    isGenerating.value = true
    try {
      const labels = await generateLabelsForPlaceholder(form.value.placeholder_name)
      if (labels) {
        form.value.german_label = labels.german_label || form.value.german_label
        form.value.english_label = labels.english_label || form.value.english_label
        toast({
          title: 'Labels generated',
          description: 'AI has suggested labels for your placeholder'
        })
      }
    } catch (error) {
      toast({
        title: 'Generation failed',
        description: 'Failed to generate labels with AI',
        variant: 'destructive'
      })
    } finally {
      isGenerating.value = false
    }
  }

  const savePlaceholder = async () => {
    if (!isFormValid.value) return

    isSaving.value = true
    try {
      await updatePlaceholder(route.params.uuid as string, form.value)
      originalForm.value = { ...form.value }
      toast({
        title: 'Placeholder updated',
        description: 'Changes have been saved successfully'
      })
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update placeholder',
        variant: 'destructive'
      })
    } finally {
      isSaving.value = false
    }
  }

  onMounted(async () => {
    await fetchPlaceholder(route.params.uuid as string)
    if (placeholder.value) {
      const formData = {
        placeholder_name: placeholder.value.placeholder_name,
        german_label: placeholder.value.german_label || '',
        english_label: placeholder.value.english_label || ''
      }
      form.value = { ...formData }
      originalForm.value = { ...formData }
    }
  })
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Placeholders', to: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDERS },
          {
            title: placeholder?.placeholder_name || 'Edit Placeholder',
            to: ROUTES.ADMIN.WORD_TEMPLATES.PLACEHOLDER_DETAIL.replace(
              ':uuid',
              route.params.uuid as string
            )
          }
        ]"
      />

      <div class="flex items-center justify-end gap-x-3">
        <Button
          variant="default"
          class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-4 py-2
            text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
            focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          :disabled="isSaving || !isFormValid || !hasChanges"
          @click="savePlaceholder"
        >
          <Loader2
            v-if="isSaving"
            class="mr-2 h-4 w-4 animate-spin"
          />
          Save Changes
        </Button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
    >
      <div class="flex items-center justify-center">
        <Loader2 class="h-8 w-8 animate-spin" />
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
    >
      <form
        class="space-y-6"
        @submit.prevent="savePlaceholder"
      >
        <div class="space-y-4">
          <div>
            <InputField
              v-model="form.placeholder_name"
              name="placeholder_name"
              label="Placeholder Name"
              placeholder="e.g., client_first_name"
              :disabled="true"
              class="font-mono"
            />
            <p class="mt-1 text-sm text-muted-foreground">
              The technical name used in templates. Cannot be changed.
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <InputField
                v-model="form.german_label"
                name="german_label"
                label="German Label"
                placeholder="e.g., Vorname des Kunden"
              />
              <p class="mt-1 text-sm text-muted-foreground">Human-readable label in German</p>
            </div>

            <div>
              <InputField
                v-model="form.english_label"
                name="english_label"
                label="English Label"
                placeholder="e.g., Client First Name"
              />
              <p class="mt-1 text-sm text-muted-foreground">Human-readable label in English</p>
            </div>
          </div>

          <div class="flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              class="rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-black shadow-sm
                hover:bg-gray-100 hover:text-gray-900"
              :disabled="!form.placeholder_name || isGenerating"
              @click="generateLabels"
            >
              <Sparkles
                v-if="!isGenerating"
                class="mr-2 h-4 w-4"
              />
              <Loader2
                v-else
                class="mr-2 h-4 w-4 animate-spin"
              />
              Generate Labels with AI
            </Button>
            <span class="text-sm text-muted-foreground">
              AI will suggest labels based on the placeholder name
            </span>
          </div>
        </div>

        <Separator />

        <div class="space-y-4">
          <h3 class="text-lg font-semibold">Usage Information</h3>

          <div class="grid gap-4 md:grid-cols-3">
            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-2xl font-bold">{{ placeholder?.template_count ?? 0 }}</div>
              <div class="text-sm text-muted-foreground">Templates using this placeholder</div>
            </div>

            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-sm font-medium">Created</div>
              <div class="text-sm text-muted-foreground">
                {{ placeholder?.created_at ? formatDate(placeholder.created_at) : 'N/A' }}
              </div>
            </div>

            <div
              class="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800"
            >
              <div class="text-sm font-medium">Last Updated</div>
              <div class="text-sm text-muted-foreground">
                {{ placeholder?.updated_at ? formatDate(placeholder.updated_at) : 'N/A' }}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
